import { portalLinkBase } from '@/lib/current-firm';
import { supabase } from '@/lib/supabase';
import type {
  ProjectRow, RoomRow, SpecRow,
  ScheduleEntryRow, PaletteColorRow, FileRow, QuestionnaireRow,
  RoomImageRow, ChangeOrderRow, RoomFeedbackRow,
} from '@/types/database';

export interface SharedProject {
  project: Pick<ProjectRow,
    'id' | 'name' | 'client_name' | 'address' | 'description' | 'hero_image_url'
    | 'start_date' | 'target_completion_date' | 'budget_cents'>;
  // A plain Pick<> since migration 0027. It was a hand-written intersection
  // while `room_type` was a name joined in from room_types; room types are
  // gone, and location is now the room's own `is_exterior` column.
  rooms: Pick<RoomRow,
    'id' | 'name' | 'status' | 'sort_order' | 'notes' | 'hero_image_url'
    | 'approved_at' | 'approved_by_name' | 'completed_at' | 'is_exterior'>[];
  specs: Pick<SpecRow,
    'id' | 'room_id' | 'item' | 'vendor' | 'quantity' | 'unit'
    | 'unit_cost_cents' | 'status' | 'estimated_delivery' | 'notes' | 'sort_order'
    | 'source_url' | 'thumbnail_url'>[];
  room_images: Pick<RoomImageRow,
    'id' | 'room_id' | 'kind' | 'storage_path' | 'caption' | 'sort_order'>[];
  // 0048 — NOT here any more, deliberately. Project notes are the designer's
  // own, and `get_shared_project` stopped returning them. Leaving the field
  // declared is what broke the live share page on 2026-09-30: the payload had
  // no `notes`, the type said it did, and `d.notes.length` threw before
  // anything rendered. A type that describes a payload it no longer gets is
  // worse than no type — it switches the compiler off exactly where it was
  // needed.
  //
  // A room's own `notes` (rooms[].notes) is a different column and stays: that
  // one is written for the client to read.
  // Replaced milestones in 0021. Filtered to upcoming in the view, using the
  // same helper the designer's dashboard uses.
  schedule: Pick<ScheduleEntryRow,
    'id' | 'room_id' | 'task' | 'start_date' | 'end_date' | 'status' | 'sort_order'>[];
  palette: Pick<PaletteColorRow, 'id' | 'name' | 'hex' | 'vendor' | 'code' | 'sort_order' | 'room_id'>[];
  files: Pick<FileRow, 'id' | 'name' | 'file_type' | 'room_id' | 'size_bytes' | 'storage_path' | 'created_at'>[];
  questionnaire: Pick<QuestionnaireRow, 'id' | 'title' | 'responses' | 'submitted_at'> | null;
  room_feedback: Pick<RoomFeedbackRow,
    'id' | 'room_id' | 'author_name' | 'body' | 'resolved_at' | 'created_at'>[];
  change_orders: Pick<ChangeOrderRow,
    'id' | 'number' | 'title' | 'scope' | 'cost_delta_cents' | 'designer_fee_cents' | 'status'
    | 'sent_at' | 'acknowledged_at' | 'acknowledged_by_name' | 'created_at'>[];
  /**
   * 0047 — the questions this client asked and the answers they were given.
   *
   * Only those two kinds: the rest of the trail (created, sent, approved, the
   * recorded IP) is the firm's record, and `get_shared_project` never returns
   * it. Absent on a portal served before 0047, hence optional.
   */
  change_order_questions?: {
    id: string;
    change_order_id: string;
    kind: 'question_asked' | 'question_answered';
    author_name: string | null;
    body: string | null;
    at: string;
  }[];
}

export async function rotateShareToken(
  projectId: string,
  expiresAt: string | null,
): Promise<string> {
  const { data, error } = await supabase.rpc('gen_share_token');
  if (error) throw error;
  const token = String(data);
  const { error: updErr } = await supabase
    .from('projects')
    .update({ share_token: token, share_token_expires_at: expiresAt })
    .eq('id', projectId);
  if (updErr) throw updErr;
  return token;
}

export async function clearShareToken(projectId: string): Promise<void> {
  const { error } = await supabase
    .from('projects')
    .update({ share_token: null, share_token_expires_at: null })
    .eq('id', projectId);
  if (error) throw error;
}

export async function fetchSharedProject(token: string): Promise<SharedProject | null> {
  const { data, error } = await supabase.rpc('get_shared_project', { token });
  if (error) throw error;
  return (data as SharedProject | null) ?? null;
}

export function buildShareUrl(token: string): string {
  return `${portalLinkBase()}#/share/${token}`;
}

/**
 * Client-side room approval, mirroring acknowledgeChangeOrderViaShare.
 *
 * Both go through SECURITY DEFINER RPCs that validate the share token server
 * side — the token is the whole authorization story, so nothing here is
 * trusted. A `false` return means the RPC refused (expired link, wrong
 * project's room, already approved), not that the network failed.
 */
export async function approveRoomViaShare(
  token: string,
  roomId: string,
  typedName: string,
): Promise<boolean> {
  const { data, error } = await supabase.rpc('approve_room', {
    token, p_room_id: roomId, typed_name: typedName,
  });
  if (error) throw error;
  return data === true;
}

/** Client pushback on a room. Retracts an existing approval server-side. */
export async function submitRoomFeedbackViaShare(
  token: string,
  roomId: string,
  typedName: string,
  body: string,
): Promise<boolean> {
  const { data, error } = await supabase.rpc('submit_room_feedback', {
    token, p_room_id: roomId, typed_name: typedName, p_body: body,
  });
  if (error) throw error;
  return data === true;
}
